import { useImperativeHandle, useRef, forwardRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useAtomValue } from "jotai";

import useUpdateMultipleText from "../../../api/api-hooks/records/useUpdateMultipleText";
import SingleSelectRecord from "../../../components/Records/Selects/SingleSelectRecord";
import CurrencyIntervalRecord from "../../../components/Records/CurrencyIntervalRecord";
import useUpdateRecordItems from "../../../api/api-hooks/records/useUpdateRecordItems";
import MultiSelectRecord from "../../../components/Records/Selects/MultiSelectRecord";
import ReferenceRecord from "../../../components/Records/References/ReferenceRecord";
import LocationRecord from "../../../components/Records/Location/LocationRecord";
import SubitemRecord from "../../../components/Records/Subitems/SubitemRecord";
import useUpdateRecord from "../../../api/api-hooks/records/useUpdateRecord";
import CurrencyRecord from "../../../components/Records/CurrencyRecord";
import DateTimeRecord from "../../../components/Records/DateTimeRecord";
import RichTextRecord from "../../../components/Records/RichTextRecord";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import ToggleRecord from "../../../components/Records/ToggleRecord";
import ObjectRecord from "../../../components/Records/ObjectRecord";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import MediaRecord from "../../../components/Records/MediaRecord";
import AttachmentsRecord from "../Attachments/AttachmentsRecord";
import TagRecord from "../../../components/Records/TagRecord";
import usePage from "../../../api/api-hooks/pages/usePage";
import { currentViewAtom } from "../../../atoms/ViewAtoms";
import useUser from "../../../api/api-hooks/auth/useUser";
import ComplexRecord from "../ComplexField/ComplexRecord";
import CalculatedRecord from "../CalculatedRecord";
import FilterRecord from "../Filter/FilterRecord";
import Constants from "../../../utils/Constants";
import StrUtils from "../../../utils/StrUtils";
import ColorRecord from "../Color/ColorRecord";
import TextRecord from "../Text/TextRecord";

/**
 * FieldFormatter - Higher order component for displaying field values.
 *
 * @param {*} field - The field schema for this field
 * @param {*} value - The current value of the field
 * @param {*} record - The parent record that contains this field
 * @param {*} recordID - The ID of the record to update with this component.
 * @param {*} moduleAPIName - The APIName of the module the record/field are in.
 * @param {*} trigger - The trigger to use for the field (click, double click)
 * @param {*} size - The size of the input (sm, md, lg)
 * @param {*} table - Whether or not the field is in a table
 * @param {*} forceReadOnly - Whether or not to force the field to be read only
 * @param {*} subitemParentID - The ID of the parent record for a Subitem/MultiSubitem field
 * @param {*} customDisplayComponent - A custom component to be used for displaying the field
 * @param {*} multiSubitemID - The ID of the MultiSubitem field
 * @param {*} lookUpID - If provided, the field is a reference, the ID of the lookup field
 * @param {*} lookUpModuleAPIName - Needed to update a lookup (reference module)
 * @param {*} lookUpField - Needed to update a lookup (reference field)
 * @param {*} parentRecord - The parent record of the current record
 * @param {*} isDrawer - Whether or not the field is being rendered on a drawer
 * @param {*} createMode - Whether or not the field is being rendered in a create mode
 * @param {*} onChangeCreate - Callback function for when the field is being rendered in createMode = true
 */
const FieldFormatter = (
  {
    field,
    value,
    record,
    recordID,
    moduleAPIName,
    trigger = "click",
    size = "sm",
    table = false,
    forceReadOnly,
    subitemParentID = null,
    customDisplayComponent = null,
    multiSubitemID = null,
    lookUpID = null,
    lookUpModuleAPIName,
    lookUpField,
    parentRecord,
    isDrawer,
    updateRecordCustom,
    updateRecordItemsCustom,
    updateMultiTextCustom,
    createMode = false,
    onChangeCreate,
    reusableViewReferenceFields,
  },
  ref
) => {
  const inputRef = useRef();

  const { user } = useUser();
  const { pageID, workspaceID } = useParams();
  const { page } = usePage(pageID);
  const { cPermissions } = useCloudPermissions();
  const {
    updateRecord: ur,
    recordUpdating,
    updateRecordError,
  } = useUpdateRecord(false, recordID);
  const {
    updateRecordItems: uri,
    itemsUpdating,
    updateRecordItemsError,
  } = useUpdateRecordItems(recordID);
  const {
    updateMultipleText: umt,
    multipleTextUpdating,
    updateMultipleTextError,
  } = useUpdateMultipleText();

  // Allow for custom overrides of functions
  const updateRecord = updateRecordCustom || ur;
  const updateRecordItems = updateRecordItemsCustom || uri;
  const updateMultipleText = updateMultiTextCustom || umt;

  const view = useAtomValue(currentViewAtom);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (field.ReadOnly) return;
      inputRef?.current?.focus();
    },
  }));

  const editedRecordInPageActivity = {
    id: StrUtils.uuidv4(),
    Type: "edited-record-in-page",
    Page: {
      id: page?.id,
      Name: page?.Name,
      Logo: page?.Icon,
    },
    View: {
      id: view?.id,
      Name: view?.Name,
      Type: view?.Type,
    },
  };

  // Override the original updateRecord function adding different recordID / subitemID for subitem fields.
  const updateSubRecord = (variables) => {
    updateRecord({
      ...variables,
      recordID: subitemParentID,
      subitemID: multiSubitemID,
      moduleAPIName: moduleAPIName,
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: editedRecordInPageActivity,
      },
    });
  };

  const updateLookUpRecord = (variables) => {
    updateRecord({
      ...variables,
      moduleAPIName: moduleAPIName,
      lookUpModuleAPIName: lookUpModuleAPIName,
      lookUpID: lookUpID,
      lookUpField: lookUpField,
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: editedRecordInPageActivity,
      },
    });
  };

  const onUpdateMultipleText = (variables) => {
    if (createMode)
      return onChangeCreate(
        { [field.APIName]: variables.payload },
        recordID,
        field.APIName
      );

    if (subitemParentID) {
      return updateMultipleText({
        ...variables,
        subitemParentID: subitemParentID,
        recordID: recordID,
        moduleAPIName: moduleAPIName,
      });
    }

    return updateMultipleText({
      ...variables,
      moduleAPIName: moduleAPIName,
    });
  };

  const onUpdate = subitemParentID
    ? updateSubRecord
    : lookUpID
    ? updateLookUpRecord
    : createMode
    ? (variables) => {
        onChangeCreate(variables.payload, recordID, field.APIName);
        callFunctionIfExists(variables?.onSuccess);
      }
    : (variables) =>
        updateRecord({
          ...variables,
          moduleAPIName: moduleAPIName,
          _UserActivity: {
            WorkspaceID: workspaceID,
            Activity: editedRecordInPageActivity,
          },
        });

  if (!field) {
    return typeof value === "string" ? value : "null";
  }

  let mode = field?.ReadOnly ? "r" : "rw";
  const instanceID = StrUtils.uuidv4();
  let type = field.FieldType;
  if (forceReadOnly) {
    mode = "r";
  }

  if (parentRecord?._LockedBy) {
    if (cPermissions.cloud_unlock_records) {
      mode = "rw";
    } else if (
      parentRecord.CreatedBy.id !== user.id &&
      parentRecord._LockedBy !== user.id
    ) {
      mode = "r";
    }
  }

  if (record?._LockedBy && !parentRecord) {
    if (cPermissions.cloud_unlock_records) {
      mode = "rw";
    } else if (
      record.CreatedBy.id !== user.id &&
      record._LockedBy !== user.id
    ) {
      mode = "r";
    }
  }

  // Force read only on lookup fields
  if (lookUpID || lookUpID === "No ID") {
    mode = "r";
  }

  if (!field || !type) {
    return typeof value === "string" ? value : "null";
  }

  if (
    type === Constants.FIELDTYPES.EMAIL ||
    type === Constants.FIELDTYPES.URL
  ) {
    return (
      <ComplexRecord
        ref={inputRef}
        onUpdate={onUpdateMultipleText}
        submitting={multipleTextUpdating}
        error={updateRecordError}
        recordID={recordID}
        type={type}
        field={field}
        existingValue={value || []}
        APIName="Email"
        mode={mode}
        tableView={table}
        isDrawer={isDrawer}
      />
    );
  }

  if (
    type === Constants.FIELDTYPES.TEXT ||
    type === Constants.FIELDTYPES.LONG_TEXT ||
    type === Constants.FIELDTYPES.NUMBER ||
    type === Constants.FIELDTYPES.PERCENT
  ) {
    return (
      <TextRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        inputSize={size}
        mode={mode}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
        isDrawer={isDrawer}
      />
    );
  }

  if (
    type === Constants.FIELDTYPES.DATE ||
    type === Constants.FIELDTYPES.DATE_TIME ||
    type === Constants.FIELDTYPES.TIME
  ) {
    mode =
      field.APIName === "Created" || field.APIName === "LastUpdated"
        ? "r"
        : mode;

    return (
      <DateTimeRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        inputSize={size}
        mode={mode}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  if (type === Constants.FIELDTYPES.PHONE) {
    return (
      <ComplexRecord
        ref={inputRef}
        onUpdate={onUpdateMultipleText}
        submitting={multipleTextUpdating}
        error={updateRecordError}
        recordID={recordID}
        type={type}
        field={field}
        existingValue={value || []}
        APIName="Phone"
        mode={mode}
        tableView={table}
        isDrawer={isDrawer}
      />
    );
  }

  if (type === Constants.FIELDTYPES.LOCATION) {
    const onUpdateLocation = () => {
      if (createMode) {
        return ({ payload }) => {
          onChangeCreate({ [field.APIName]: payload }, recordID, field.APIName);
        };
      }

      if (subitemParentID) {
        return (variables) => {
          updateRecordItems({
            ...variables,
            subitemParentID: subitemParentID,
            recordID: recordID,
            moduleAPIName: moduleAPIName,
          });
        };
      }

      return (variables) => {
        updateRecordItems({ ...variables, moduleAPIName: moduleAPIName });
      };
    };

    return (
      <LocationRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        existingValues={value || []}
        modifyTrigger={trigger}
        inputSize={size}
        mode={mode}
        onUpdate={onUpdateLocation()}
        submitting={itemsUpdating}
        error={updateRecordError}
        createMode={createMode}
      />
    );
  }

  if (type === Constants.FIELDTYPES.SINGLE_REFERENCE) {
    return (
      <ReferenceRecord
        ref={inputRef}
        tableView={table}
        record={record}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        mode={mode}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
        customDisplayComponent={customDisplayComponent}
        reusableViewReferenceFields={reusableViewReferenceFields}
        pageID={pageID}
      />
    );
  }

  if (type === Constants.FIELDTYPES.MULTI_REFERENCE) {
    const onClear = ({ onSuccess }) => {
      if (subitemParentID) {
        updateRecord({
          field: field,
          recordID: subitemParentID,
          subitemID: recordID,
          payload: { [field.APIName]: [] },
          onSuccess: onSuccess,
          moduleAPIName: moduleAPIName,
          _UserActivity: {
            WorkspaceID: workspaceID,
            Activity: editedRecordInPageActivity,
          },
        });

        return;
      }

      updateRecord({
        field: field,
        recordID: recordID,
        payload: { [field.APIName]: [] },
        onSuccess: onSuccess,
        moduleAPIName: moduleAPIName,
        _UserActivity: {
          WorkspaceID: workspaceID,
          Activity: editedRecordInPageActivity,
        },
      });
    };

    const onUpdateMultiReference = () => {
      if (createMode) {
        return ({ payload }) => {
          onChangeCreate({ [field.APIName]: payload }, recordID, field.APIName);
        };
      }

      if (subitemParentID) {
        return (variables) => {
          updateRecordItems({
            ...variables,
            subitemParentID: subitemParentID,
            recordID: recordID,
            moduleAPIName: moduleAPIName,
          });
        };
      }

      return (variables) => {
        updateRecordItems({ ...variables, moduleAPIName: moduleAPIName });
      };
    };

    return (
      <ReferenceRecord
        ref={inputRef}
        tableView={table}
        parentRecord={record}
        record={record}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        mode={mode}
        onUpdate={onUpdateMultiReference()}
        submitting={itemsUpdating}
        error={updateRecordItemsError}
        customDisplayComponent={customDisplayComponent}
        createMode={createMode}
        pageID={pageID}
        reusableViewReferenceFields={reusableViewReferenceFields}
      />
    );
  }

  if (type === Constants.FIELDTYPES.SINGLE_SELECT) {
    return (
      <SingleSelectRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        value={value}
        modifyTrigger={trigger}
        mode={mode}
        instanceID={instanceID}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
        table={table}
      />
    );
  }

  if (type === Constants.FIELDTYPES.MULTI_SELECT) {
    return (
      <MultiSelectRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        value={value}
        modifyTrigger={trigger}
        mode={mode}
        onUpdate={onUpdate}
        itemsUpdating={itemsUpdating}
        error={updateRecordItemsError}
        table={table}
      />
    );
  }

  if (type === Constants.FIELDTYPES.TOGGLE) {
    return (
      <ToggleRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        value={value}
        modifyTrigger={trigger}
        mode={mode}
        instanceID={instanceID}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  if (type === Constants.FIELDTYPES.CURRENCY) {
    return (
      <CurrencyRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        mode={mode}
        instanceID={instanceID}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  if (type === Constants.FIELDTYPES.CURRENCY_INTERVAL) {
    return (
      <CurrencyIntervalRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        mode={mode}
        instanceID={instanceID}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  if (type === Constants.FIELDTYPES.RICH_TEXT) {
    return (
      <RichTextRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        value={value && typeof value === "object" ? value.RichText : value}
        plaintext={value && typeof value === "object" ? value.PlainText : null}
        modifyTrigger={trigger}
        mode={mode}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  if (type === Constants.FIELDTYPES.OBJECT) {
    return (
      <ObjectRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        type={type}
        field={field}
        value={value}
        modifyTrigger={trigger}
        readonly={false}
        mode={mode}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  if (type === Constants.FIELDTYPES.SINGLE_FILE) {
    return (
      <AttachmentsRecord
        ref={inputRef}
        tableView={table}
        recordID={recordID}
        subitemID={multiSubitemID}
        type={type}
        field={field}
        existingValues={value || []}
        modifyTrigger={trigger}
        mode={mode}
        onUpdate={onUpdate}
        moduleAPIName={moduleAPIName}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  if (type === Constants.FIELDTYPES.IMAGE) {
    return (
      <MediaRecord
        ref={inputRef}
        recordID={recordID}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        subitemID={multiSubitemID}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
        table={table}
        type={Constants.FIELDTYPES.IMAGE}
        mode={mode}
        moduleAPIName={moduleAPIName}
      />
    );
  }

  if (type === Constants.FIELDTYPES.VIDEO) {
    return (
      <MediaRecord
        ref={inputRef}
        type={Constants.FIELDTYPES.VIDEO}
        subitemID={multiSubitemID}
        recordID={recordID}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
        table={table}
        mode={mode}
        moduleAPIName={moduleAPIName}
      />
    );
  }

  if (type === Constants.FIELDTYPES.AUDIO) {
    return (
      <MediaRecord
        ref={inputRef}
        type={Constants.FIELDTYPES.AUDIO}
        subitemID={multiSubitemID}
        recordID={recordID}
        field={field}
        existingValue={value}
        modifyTrigger={trigger}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
        table={table}
        mode={mode}
        moduleAPIName={moduleAPIName}
      />
    );
  }

  if (
    type === Constants.FIELDTYPES.SUBITEM ||
    type === Constants.FIELDTYPES.MULTI_SUBITEM
  ) {
    return (
      <SubitemRecord
        tableView={table}
        recordID={recordID}
        parentRecord={record}
        type={type}
        field={field}
        existingValue={value}
        mode={mode}
        displayMode={
          type === "Subitem"
            ? "SummaryCard"
            : type === "MultiSubitem"
            ? "SummaryTable"
            : "SummaryCard"
        }
        moduleAPIName={moduleAPIName}
        reusableViewReferenceFields={reusableViewReferenceFields}
      />
    );
  }

  if (type === "Tags") {
    return (
      <TagRecord
        ref={inputRef}
        tableView={table}
        mode={mode}
        modifyTrigger={trigger}
        tags={value}
        moduleAPIName={moduleAPIName}
        recordID={subitemParentID || recordID}
        field={field}
        subitemAPIName={subitemParentID ? field?.SubitemParent : null}
        multiSubitemID={multiSubitemID}
      />
    );
  }

  if (type === "Calculated") {
    return (
      <CalculatedRecord
        tableView={table}
        type={type}
        field={field}
        existingValue={value}
        inputSize={size}
        mode={"r"}
        moduleAPIName={moduleAPIName}
      />
    );
  }

  if (type === "Filter") {
    return (
      <FilterRecord
        type={type}
        field={field}
        existingValue={value}
        mode={mode}
        moduleAPIName={moduleAPIName}
        recordID={recordID}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  if (type === "Color") {
    return (
      <ColorRecord
        type={type}
        field={field}
        existingValue={value}
        mode={mode}
        moduleAPIName={moduleAPIName}
        recordID={recordID}
        onUpdate={onUpdate}
        submitting={recordUpdating}
        error={updateRecordError}
      />
    );
  }

  return typeof value === "object" ? JSON.stringify(value) : value;
};

export default forwardRef(FieldFormatter);
